'use client';

import { useCallback, useEffect, useState } from 'react';
import { PermissionGuard } from '@/components/common/PermissionGuard';
import { useTranslation } from '@/hooks/useTranslation';
import { SYSTEM_ADMIN_PERMISSION } from '@/lib/iam-constants';
import { toast } from '@/lib/toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  listPendingReviews,
  approveReview,
  revokeReview,
  type PendingUserRoleReview,
} from '@/services/api/iam-governance';

/**
 * Access Review 管理页面（规则 §5.3.15）
 *
 * 合规场景：模块 owner 每 90 天复核一次用户-角色绑定是否仍必要。
 * 超期未复核的 UserRole 会出现在本页面。
 *
 * 权限：access_review:manage 或 system:admin
 */
export default function AccessReviewPage() {
  return (
    <PermissionGuard permissions={['access_review:manage', SYSTEM_ADMIN_PERMISSION]}>
      <AccessReviewContent />
    </PermissionGuard>
  );
}

type ActionType = 'keep' | 'revoke';

function AccessReviewContent() {
  const { t } = useTranslation();
  const [items, setItems] = useState<PendingUserRoleReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);

  const [actionDialog, setActionDialog] = useState<{
    type: ActionType;
    item: PendingUserRoleReview;
  } | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listPendingReviews(days);
      setItems(data ?? []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t.iamGovernance.accessReview.unknownError;
      toast.error(t.iamGovernance.accessReview.loadFailed + msg);
    } finally {
      setLoading(false);
    }
  }, [days, t]);

  useEffect(() => {
    load();
  }, [load]);

  function openAction(type: ActionType, item: PendingUserRoleReview) {
    setActionDialog({ type, item });
    setComment('');
  }

  async function submitAction() {
    if (!actionDialog) return;
    const { type, item } = actionDialog;
    if (type === 'revoke' && !comment.trim()) {
      toast.error(t.iamGovernance.accessReview.revokeReasonRequired);
      return;
    }
    try {
      setSubmitting(true);
      if (type === 'keep') {
        await approveReview(item.id, comment || undefined);
        toast.success(t.common?.success ?? 'Success');
      } else {
        await revokeReview(item.id, comment);
        toast.success(t.common?.success ?? 'Success');
      }
      setActionDialog(null);
      await load();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t.iamGovernance.accessReview.unknownError;
      toast.error(
        (type === 'keep'
          ? t.iamGovernance.accessReview.keepFailed
          : t.iamGovernance.accessReview.revokeFailed) + msg,
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t.iamGovernance.accessReview.title}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t.iamGovernance.accessReview.subtitle}
          </p>
        </div>
        <label className="text-sm">
          {t.iamGovernance.accessReview.threshold}
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="ml-2 rounded border border-gray-300 px-2 py-1 text-sm"
          >
            <option value={60}>{t.iamGovernance.accessReview.thresholdOptions.d60}</option>
            <option value={90}>{t.iamGovernance.accessReview.thresholdOptions.d90}</option>
            <option value={120}>{t.iamGovernance.accessReview.thresholdOptions.d120}</option>
          </select>
        </label>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-400">{t.iamGovernance.accessReview.loading}</div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left">{t.iamGovernance.accessReview.columns.user}</th>
                <th className="px-4 py-3 text-left">{t.iamGovernance.accessReview.columns.role}</th>
                <th className="px-4 py-3 text-left">{t.iamGovernance.accessReview.columns.organization}</th>
                <th className="px-4 py-3 text-left">{t.iamGovernance.accessReview.columns.createdAt}</th>
                <th className="px-4 py-3 text-left">{t.iamGovernance.accessReview.columns.lastReviewed}</th>
                <th className="px-4 py-3 text-right">{t.iamGovernance.accessReview.columns.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                    {t.iamGovernance.accessReview.empty}
                  </td>
                </tr>
              )}
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3">
                    {item.user?.displayName || item.user?.username || item.userId}
                  </td>
                  <td className="px-4 py-3">{item.role?.name || item.roleId}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {item.organizationId || t.iamGovernance.accessReview.systemLevel}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {new Date(item.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {item.lastReviewedAt
                      ? new Date(item.lastReviewedAt).toLocaleDateString()
                      : t.iamGovernance.accessReview.neverReviewed}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => openAction('keep', item)}
                      className="mr-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                      {t.iamGovernance.accessReview.keep}
                    </button>
                    <button
                      onClick={() => openAction('revoke', item)}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      {t.iamGovernance.accessReview.revoke}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!actionDialog} onOpenChange={(o) => !o && setActionDialog(null)}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>
              {actionDialog?.type === 'keep'
                ? t.iamGovernance.accessReview.keepDialogTitle
                : t.iamGovernance.accessReview.revokeDialogTitle}
            </DialogTitle>
            <DialogDescription>
              {actionDialog?.type === 'keep'
                ? t.iamGovernance.accessReview.keepDialogDesc
                : t.iamGovernance.accessReview.revokeDialogDesc}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label>
              {actionDialog?.type === 'revoke'
                ? t.iamGovernance.accessReview.commentRequired
                : t.iamGovernance.accessReview.commentOptional}
            </Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder={
                actionDialog?.type === 'keep'
                  ? t.iamGovernance.accessReview.keepPlaceholder
                  : t.iamGovernance.accessReview.revokePlaceholder
              }
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)} disabled={submitting}>
              {t.iamGovernance.accessReview.cancel}
            </Button>
            <Button
              onClick={submitAction}
              disabled={submitting}
              className={
                actionDialog?.type === 'revoke' ? 'bg-red-600 hover:bg-red-700' : ''
              }
            >
              {submitting ? t.iamGovernance.accessReview.submitting : t.iamGovernance.accessReview.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
